/*
 * Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 * SPDX-License-Identifier: MIT-0
 */

/*
  The AuthInterceptor intercepts HTTP requests and adds an Authorization header 
  with a JWT token obtained from AWS Cognito via AWS Amplify. 
  It ensures that authenticated requests to protected API endpoints include 
  the necessary authentication token for authorization.
*/

import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
} from '@angular/common/http';
import { from, Observable } from 'rxjs';
import { Auth } from 'aws-amplify';
import { filter, map, switchMap } from 'rxjs/operators';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor() {}
  idToken = '';

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {


    // Checks if the request URL includes 'tenant/init'. 
    // If true, it allows the request to proceed without interception by returning next.handle(req).
    //
    if (req.url.includes('tenant/init')) {
      return next.handle(req);
    }

    // Local bundled assets (i18n translation files, etc.) are public static files that need no
    // Authorization header. They must never go through the Cognito path below, because a failed
    // Auth.currentSession() there drops the request entirely and the app boots with untranslated keys.
    if (req.url.includes('assets/')) {
      return next.handle(req);
    }

    // If Cognito config is not yet loaded (e.g., during initial app load, tenant/language selection, or fetching config),
    // skip adding the Authorization header. Only public endpoints should be called before Amplify is configured.
    // Once config is loaded, authenticated requests will include the auth header as expected.
    const userPoolId = localStorage.getItem('userPoolId');
    const appClientId = localStorage.getItem('appClientId');
    if (!userPoolId || !appClientId) {
      // Amplify not configured, skip auth
      return next.handle(req);
    }

    const s = Auth.currentSession().catch((err) => console.log(err));
    const session$ = from(s);

    return session$.pipe(
      filter((sesh) => !!sesh),
      map((sesh) => (!!sesh ? sesh.getIdToken().getJwtToken() : '')),
      switchMap((tok) => {
        req = req.clone({
          headers: req.headers.set('Authorization', 'Bearer ' + tok),
        });
        return next.handle(req);
      })
    );
  }
}
